/* istanbul ignore file */

import { breakpoint } from '@porsche-design-system/components-js/styles';
import { LatLong } from '../entities/places';
import { COOKIE_DOMAIN, MS_IN_MONTH } from '../utility/constants';
import { NaviError, NaviErrorType, NaviErrorTypes } from '../utility/navi-error';
import {
  camelToKebapCase,
  getHTMLElement,
  getString,
  hasKey,
  isString,
  shallowCopySpecifiedAttributesWithDefault
} from '../utility/helper';

type CheckConsent = (args: {
  processorId: string;
  timeoutVar: Record<string, never>;
  mapContainer: string;
  callbacks: {
    success: () => void;
  };
}) => void;

export type MarketingWindow = Window & {
  GlobalConsent?: { Processor?: { googleMaps?: string } };
  PCOM?: { globalConsent?: { checkConsent?: CheckConsent } };
  ucPrivacyShield?: { checkConsent?: CheckConsent };
};

class WindowService {
  get location(): Location | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.location;
  }

  set location(newLocation: Location | null) {
    if (newLocation) {
      window.location = newLocation;
    }
  }

  get sessionStorage(): Storage {
    return window.sessionStorage;
  }

  get localStorage(): Storage {
    return window.localStorage;
  }

  set version(version: string) {
    Object.assign(window, { PHN_HEADER_VERSION: version });
  }

  set navigationLoaded(loaded: boolean) {
    Object.assign(window, { PHN_NAVIGATION_LOADED: loaded });
  }

  get bodyOverflow(): string {
    return document.body.style.overflow;
  }

  get bodyHeight(): string {
    return document.body.style.height;
  }

  requestAnimationFrame(callback: FrameRequestCallback): number {
    return window.requestAnimationFrame(callback);
  }

  createDiv() {
    return window.document.createElement('div');
  }

  getBreakpoint(): number {
    const clientWidth = Math.max(window.document.documentElement.clientWidth || 0, window.innerWidth || 0);

    const matchingBreakpoint = Object.keys(breakpoint)
      .reverse()
      .find((foundBreakpoint) => hasKey(breakpoint, foundBreakpoint) && clientWidth >= breakpoint[foundBreakpoint]);

    return typeof matchingBreakpoint === 'string' && hasKey(breakpoint, matchingBreakpoint)
      ? breakpoint[matchingBreakpoint]
      : 0;
  }

  get redirected(): boolean {
    return (
      (window as Window & { REDIRECTED?: boolean }).REDIRECTED === true ||
      window.location.search.includes('cs_redirect=')
    );
  }

  createIframe(id: string, src: string) {
    const iframe = window.document.createElement('iframe');
    iframe.id = id;
    iframe.style.display = 'none';
    iframe.src = src;

    window.document.body.appendChild(iframe);
  }

  removeElement(id: string) {
    window.document.getElementById(id)?.remove();
  }

  redirectToUrl(url: string) {
    window.location.assign(url);
  }

  generateRandom(size: number): string {
    const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    const buffer = new Uint8Array(size);

    window.crypto.getRandomValues(buffer);

    const state: string[] = [];
    for (let i = 0; i < buffer.byteLength; i += 1) {
      const index = buffer[i] % CHARSET.length;
      state.push(CHARSET[index]);
    }

    return state.join('');
  }

  /**
   * site does NOT have consent mechanism -> consent NOT needed
   * site does have consent mechanism + isConsentActive NOT exists -> consent NOT needed
   * site does have consent mechanism + isConsentActive exists + isConsentActive -> consent needed
   * site does have consent mechanism + NOT isConsentActive -> consent NOT needed
   */

  /**
   * consent needed + consent given -> userConsent = true
   * consent needed + consent NOT given -> userConsent = false
   * consent NOT needed -> userConsent = true
   */
  maybeCheckMarketingConsent(handleSuccess: () => void): boolean {
    const args = {
      processorId: (window as MarketingWindow).GlobalConsent?.Processor?.googleMaps ?? 'Google Maps',
      timeoutVar: {},
      mapContainer: '',
      callbacks: {
        success: handleSuccess
      }
    };

    if (typeof (window as MarketingWindow).PCOM?.globalConsent?.checkConsent === 'function') {
      (window as MarketingWindow).PCOM?.globalConsent?.checkConsent?.(args);
      return true;
    }

    if (typeof (window as MarketingWindow).ucPrivacyShield?.checkConsent === 'function') {
      (window as MarketingWindow).ucPrivacyShield?.checkConsent?.(args);
      return true;
    }

    handleSuccess();
    return false;
  }

  async deriveChallenge(code: string): Promise<string> {
    if (code.length < 43 || code.length > 128) {
      throw new NaviError('invalid code length', NaviErrorTypes.INVALID_INPUT);
    }

    const buf = new ArrayBuffer(code.length);
    const bufView = new Uint8Array(buf);

    for (let i = 0; i < code.length; i++) {
      bufView[i] = code.charCodeAt(i);
    }

    const buffer = await window.crypto.subtle.digest('SHA-256', bufView);

    const bytes = new Uint8Array(buffer);

    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  async hashString(text: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  getScrollbarWidth(): string {
    const scrollDiv = window.document.createElement('div');
    scrollDiv.style.overflow = 'scroll';
    window.document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    window.document.body.removeChild(scrollDiv);

    return Number.isNaN(scrollbarWidth) ? '0px' : `${scrollbarWidth}px`;
  }

  async fetchGoogleMapsApi(
    apiKey: string,
    url = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    // @ts-ignore
  ): Promise<typeof google> {
    // @ts-ignore
    if (window.google !== undefined && window.google.maps !== undefined && window.google.maps.version !== undefined) {
      // @ts-ignore
      return window.google;
    }

    const existingScript = document.getElementById('googleMaps'); // -> Current name?!

    if (existingScript) {
      // @ts-ignore
      return window.google;
    }

    const script = document.createElement('script');
    script.src = url;
    script.id = 'googleMaps';
    document.head.appendChild(script);

    return new Promise((resolve, reject) => {
      script.onerror = () => {
        reject(new NaviError('Failed to load google maps script.', NaviErrorTypes.GENERAL));
      };
      script.onload = () => {
        // @ts-ignore
        resolve(window.google);
      };
    });
  }

  async getNavigatorLatLong(): Promise<LatLong> {
    return new Promise((resolve, reject) => {
      window.navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          resolve({ latitude, longitude });
        },
        (error) =>
          reject(
            new NaviError(
              `geolocation.getCurrentPosition failed with ${error.code}: ${error.message}`,
              getErrorType(error)
            )
          )
      );
    });
  }

  getCookie(cookieName: string): string {
    return getString(
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${cookieName}=`))
        ?.split('=')[1]
    );
  }

  setCookie(cookieName: string, newValue: string): void {
    document.cookie = `${cookieName}=${newValue};domain=${COOKIE_DOMAIN};expires=${new Date(
      Date.now() + MS_IN_MONTH * 10
    ).toUTCString()};`;
  }

  btoa(data: string) {
    return window.btoa(data);
  }

  private initialBodyStyleValues = {
    top: '',
    position: '',
    inlineSize: '',
    overflowY: ''
  } as const;
  private initialDocumentScrollTop = 0;

  lockScroll() {
    this.initialBodyStyleValues = shallowCopySpecifiedAttributesWithDefault(
      document.body.style,
      this.initialBodyStyleValues
    );
    this.initialDocumentScrollTop = document.documentElement.scrollTop;

    document.body.style.top = `-${document.documentElement.scrollTop}px`;
    document.body.style.position = 'fixed';
    document.body.style.inlineSize = '100%';
    document.body.style.overflowY = 'scroll';
  }

  unlockScroll() {
    Object.entries(this.initialBodyStyleValues).forEach(([key, value]) =>
      document.body.style.setProperty(camelToKebapCase(key), value)
    );

    document.documentElement.scrollTop = this.initialDocumentScrollTop;
  }

  hasScrollbar(): boolean {
    return window.document.body.offsetHeight > window.innerHeight;
  }

  async waitForElement(parent: HTMLElement, selector?: string): Promise<HTMLElement> {
    if (!isString(selector)) {
      return parent;
    }

    const el = getHTMLElement(parent.querySelector(selector));
    if (el !== null) {
      return el;
    }
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const newEl = getHTMLElement(parent.querySelector(selector));
        if (newEl !== null) {
          resolve(newEl);
          observer.disconnect();
        }
      });

      observer.observe(parent, { childList: true, subtree: true });
    });
  }
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export function getErrorType(error: GeolocationPositionError): NaviErrorType {
  switch (error.code) {
    case error.PERMISSION_DENIED: {
      return NaviErrorTypes.GEOLOCATION_DENIED;
    }
    case error.POSITION_UNAVAILABLE:
    case error.TIMEOUT: {
      return NaviErrorTypes.RETRY;
    }
    default: {
      return NaviErrorTypes.GENERAL;
    }
  }
}

export default new WindowService();
